import { MEDIA_QUERIES } from 'design'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'

import { useMediaQuery } from '../../hooks/use_media_query'
import {
  WIDGET_LAYOUT,
  collectionScrollBox,
  dialogElement,
  iframeElements,
  isFirstInCollection,
} from './constants'
import { POSITION_IN_COLLECTION_EVENT } from './use_widget_layout'

export const useCollectionsScrollManagement = ({
  dialogIsVerticalScrollContainer,
  positionInCollection,
  widgetLayout,
  collectionReady,
}: any) => {
  const [isCollectionIntersecting, setIsCollectionIntersecting] =
    useState(false)
  const [scrollHeight, setScrollHeight] = useState() // just used to rerender when the scroll height changes
  const desktop = useMediaQuery(MEDIA_QUERIES.md, window.parent)
  const allWidgetsRendered =
    !desktop && widgetLayout === WIDGET_LAYOUT.FULLSCREEN
      ? collectionScrollBox.scrollHeight ===
        window.innerHeight * (iframeElements.length + 2)
      : collectionScrollBox.scrollHeight ===
        (height || 614) * (iframeElements.length + 2)
  const [initialized, setInitialized] = useState(!isFirstInCollection) // first in collection is responsible for initialising the scroll position
  const [pendingScrollRestore, setPendingScrollRestore] = useState(false) // used to keep the scroll listener off until the scroll position is restored

  useLayoutEffect(() => {
    return () => {
      if (
        positionInCollection === 0 &&
        widgetLayout === WIDGET_LAYOUT.FULLSCREEN
      ) {
        setPendingScrollRestore(true)
      }
    }
  }, [positionInCollection, widgetLayout])

  useEffect(() => {
    if (
      !desktop &&
      widgetLayout === WIDGET_LAYOUT.FULLSCREEN &&
      positionInCollection === 0
    ) {
      setPendingScrollRestore(true)
    }
  }, [widgetLayout, desktop, positionInCollection])

  useEffect(() => {
    if (positionInCollection !== 0 && pendingScrollRestore) {
      setPendingScrollRestore(false)
    }
  }, [pendingScrollRestore, positionInCollection])

  // used to determine if the collection is visible in the viewport
  useEffect(() => {
    if (!collection || widgetLayout === WIDGET_LAYOUT.FULLSCREEN) {
      return
    }
    const onIntersectionChange = (entries: IntersectionObserverEntry[]) => {
      setIsCollectionIntersecting(entries[0].isIntersecting)
    }
    const io = new IntersectionObserver(onIntersectionChange, {
      root: window.parent.document,
    })
    io.observe(collectionScrollBox)

    return () => {
      io.disconnect()
    }
  }, [widgetLayout])

  // used to track changes in the scroll height
  useLayoutEffect(() => {
    if (collectionReady) {
      const onResize = () => {
        setScrollHeight(collectionScrollBox.scrollHeight)
      }
      const io = new ResizeObserver(onResize)
      collectionScrollBox.childNodes.forEach((childNode) => {
        io.observe(childNode)
      })

      return () => {
        io.disconnect()
      }
    }
  }, [collectionReady])

  // update the selected widget when the scroll position changes
  const onScroll = useCallback(() => {
    if (!allWidgetsRendered) {
      return
    }
    // if scrolling past start
    if (collectionScrollBox.scrollTop === 0) {
      collectionScrollBox.scrollTop =
        collectionScrollBox.scrollHeight - collectionScrollBox.clientHeight * 2
    }
    // if scrolling past end
    else if (
      collectionScrollBox.scrollTop ===
      collectionScrollBox.scrollHeight - collectionScrollBox.clientHeight
    ) {
      collectionScrollBox.scrollTop = collectionScrollBox.clientHeight
    }
    // find the widget at current scroll position and set it as the selected widget
    for (let i = 0; i < iframeElements.length; i++) {
      const iframeElement = iframeElements[i]
      if (
        iframeElement.parentElement.offsetTop === collectionScrollBox.scrollTop
      ) {
        const indexOfSelected = iframeElements.indexOf(iframeElement)
        for (let i = 0; i < iframeElements.length; i++) {
          iframeElements[i].contentWindow.dispatchEvent(
            new CustomEvent(POSITION_IN_COLLECTION_EVENT, {
              detail: {
                position: indexOfSelected * -1 + i,
              },
            }),
          )
        }
        break
      }
    }
  }, [allWidgetsRendered])

  // initialise the scroll position
  useLayoutEffect(() => {
    if (isFirstInCollection && allWidgetsRendered && !initialized) {
      collectionScrollBox.scrollTop = collectionScrollBox.clientHeight
      dialogElement.style.display = 'flex'
      onScroll()
      setInitialized(true)
    }
  }, [allWidgetsRendered, initialized, onScroll])

  // restore scroll position in the embedded collection after entering/leaving fullscreen
  useEffect(() => {
    if (dialogIsVerticalScrollContainer) {
      if (
        allWidgetsRendered &&
        positionInCollection === 0 &&
        pendingScrollRestore
      ) {
        collectionScrollBox.scrollTop =
          window.frameElement.parentElement.offsetTop
        onScroll()
        setPendingScrollRestore(false)
      }
    }
  }, [
    allWidgetsRendered,
    dialogIsVerticalScrollContainer,
    onScroll,
    pendingScrollRestore,
    positionInCollection,
  ])

  // Used to create the illusion of infinite, recursive scroll.
  // There is a placeholder image of first widget at the end of the list, and a placeholder image of the last widget at the start of the list.
  // When the end of the list is reached, it allows smooth scroll animation to the placeholder image, before jumping the scroll back to the start of the list.
  // Vice versa when scrolling past the start of the list
  useLayoutEffect(() => {
    if (
      dialogIsVerticalScrollContainer &&
      positionInCollection === 0 &&
      initialized &&
      !pendingScrollRestore
    ) {
      collectionScrollBox.addEventListener('scroll', onScroll)

      return () => {
        collectionScrollBox.removeEventListener('scroll', onScroll)
      }
    }
  }, [
    allWidgetsRendered,
    dialogIsVerticalScrollContainer,
    initialized,
    onScroll,
    pendingScrollRestore,
    positionInCollection,
  ])

  return {
    isCollectionIntersecting,
  }
}

// eslint-disable-next-line
// @ts-ignore
const { collection, height } = window.speakeasySettings
